import {
  Combobox,
  FAIcon,
  Input,
  NumberInput,
  SalaryGroupCombobox,
} from '@/components'
import { DepartmentCombobox } from '@/components/combobox/department-combox'
import { SalaryCodeCombobox } from '@/components/combobox/salary-code-combobox'
import { Module } from '@/modules/companies/consts'
import { SalaryType } from '@/modules/companies/types.graphql'
import { useTranslate } from '@ur-solutions/react-hooks'
import clamp from 'lodash/clamp'
import { useHolidayAllowances } from './hooks'
import {
  SettingNote,
  SettingRow,
  SettingSwitch,
  SettingsSection,
} from './salary-settings-section'
import type { SalarySettingsTabProps } from './SalarySettingsFormView'

export const SalarySettingsGeneral: React.FC<SalarySettingsTabProps> = ({
  form,
  config,
}) => {
  const translations = useTranslate({
    standardSettings: 'settings.standard-settings',
    name: 'common.name',
    salaryGroups: 'salary.salary-groups',
    salaryPeriod: 'settings.salary-period',

    salaryType: 'settings.salary-type',
    fixedSalary: 'settings.fixed-salary',
    hourlySalary: 'settings.hourly-salary',
    dailySalary: 'settings.daily-salary',
    salaryCodeDay: 'settings.salary-code-day',
    defaultSalaryCode: 'settings.default-salary-code',
    defaultSalaryCodeWhenOvertime: 'settings.default-salary-code-when-overtime',
    transferredOvertimeToFlextime: 'salary.transfered-overtime-to-flextime',
    salaryCode: 'common.salary-code',
    salaryDayOtherSalaryCodeWarning:
      'settings.salary-day-other-salary-code-warning',

    departments: 'common.departments',
    defaultDepartment: 'settings.default-department',
    availableDepartments: 'settings.available-departments',

    dataDistribution: 'common.data-distribution',
    groupSalaryOnVehicle: 'settings.group-salary-on-vehicle',
    holidayAllowances: 'common.holiday-allowances',
    none: 'common.none',
  })

  const { formValues, formErrors, updateForm } = form
  const holidayAllowances = useHolidayAllowances()
  const isActivities = config.name === Module.ACTIVITIES

  const salaryTypeOptions = [
    { value: SalaryType.FIXED, label: translations.fixedSalary },
    { value: SalaryType.HOUR, label: translations.hourlySalary },
    ...(isActivities
      ? [{ value: SalaryType.DAY, label: translations.dailySalary }]
      : []),
  ]

  const holidayAllowanceOptions = holidayAllowances.map(holidayAllowance => ({
    value: holidayAllowance.id,
    label: `(${holidayAllowance.salaryCode?.name ?? translations.none}) ${
      holidayAllowance.day
    }`,
  }))

  function updateSalaryDateStart(salaryDateStart: number | undefined) {
    if (salaryDateStart === undefined) return
    updateForm({
      salaryDateStart,
      salaryDateEnd: clamp(
        salaryDateStart === 1 ? 31 : salaryDateStart - 1,
        1,
        31
      ),
    })
  }

  return (
    <>
      <SettingsSection title={translations.standardSettings}>
        <SettingRow label={translations.name}>
          <Input
            value={formValues.name}
            placeholder={translations.name}
            onChange={name => updateForm({ name })}
          />
        </SettingRow>
        {!formValues.userId && (
          <SettingRow label={translations.salaryGroups}>
            <SalaryGroupCombobox
              mode="multiple"
              value={formValues.salaryGroups}
              onChange={salaryGroups => updateForm({ salaryGroups })}
            />
          </SettingRow>
        )}
        <SettingRow
          label={translations.salaryPeriod}
          error={formErrors.salaryDateStart}
        >
          <div className="flex items-center gap-3">
            <NumberInput
              value={formValues.salaryDateStart}
              min={1}
              max={31}
              onChange={updateSalaryDateStart}
            />
            <FAIcon icon="arrow-right" className="text-stone-400" />
            <NumberInput value={formValues.salaryDateEnd} disabled />
          </div>
        </SettingRow>
      </SettingsSection>

      <SettingsSection title={translations.salaryType}>
        <SettingRow label={translations.salaryType}>
          <Combobox
            mode="single"
            noSearch
            items={salaryTypeOptions}
            value={formValues.salaryType as string}
            onChange={salaryType =>
              updateForm({ salaryType: salaryType as keyof typeof SalaryType })
            }
          />
        </SettingRow>
        {formValues.salaryType === SalaryType.DAY && (
          <SettingRow label={translations.salaryCodeDay}>
            <SalaryCodeCombobox
              mode="single"
              clearable
              isSalaryDay
              value={formValues.salaryDayCode ?? ''}
              onChange={code => updateForm({ salaryDayCode: code || null })}
            />
          </SettingRow>
        )}
        <SettingRow label={translations.defaultSalaryCode}>
          <SalaryCodeCombobox
            mode="single"
            clearable
            value={formValues.defaultSalaryCode ?? ''}
            onChange={code => updateForm({ defaultSalaryCode: code || null })}
          />
        </SettingRow>
        <SettingRow label={translations.defaultSalaryCodeWhenOvertime}>
          <SalaryCodeCombobox
            mode="single"
            clearable
            value={formValues.defaultSalaryCodeWhenOvertime ?? ''}
            onChange={code =>
              updateForm({ defaultSalaryCodeWhenOvertime: code || null })
            }
          />
        </SettingRow>
        <SettingRow
          label={`${translations.transferredOvertimeToFlextime} - ${translations.salaryCode}`}
        >
          <SalaryCodeCombobox
            mode="single"
            clearable
            value={formValues.absenceHourTransactionOvertimeSalaryCode ?? ''}
            onChange={code =>
              updateForm({
                absenceHourTransactionOvertimeSalaryCode: code || null,
              })
            }
          />
        </SettingRow>
        {formValues.salaryType === SalaryType.DAY && (
          <SettingNote tone="warning">
            {translations.salaryDayOtherSalaryCodeWarning}
          </SettingNote>
        )}
      </SettingsSection>

      {(config.name === Module.TIMESHEETS || isActivities) && (
        <SettingsSection title={translations.departments}>
          <SettingRow label={translations.defaultDepartment}>
            <DepartmentCombobox
              mode="single"
              clearable
              value={formValues.defaultDepartment ?? ''}
              onChange={department =>
                updateForm({ defaultDepartment: department || null })
              }
            />
          </SettingRow>
          {config.name === Module.TIMESHEETS && (
            <SettingRow label={translations.availableDepartments}>
              <DepartmentCombobox
                mode="multiple"
                value={formValues.availableDepartments}
                onChange={availableDepartments =>
                  updateForm({ availableDepartments })
                }
              />
            </SettingRow>
          )}
        </SettingsSection>
      )}

      {isActivities && (
        <SettingsSection title={translations.dataDistribution}>
          <SettingSwitch
            label={translations.groupSalaryOnVehicle}
            checked={formValues.groupSalaryOnVehicle}
            onChange={groupSalaryOnVehicle =>
              updateForm({ groupSalaryOnVehicle })
            }
          />
          <SettingRow label={translations.holidayAllowances}>
            <Combobox
              mode="multiple"
              items={holidayAllowanceOptions}
              value={formValues.holidayAllowances}
              onChange={holidayAllowances =>
                updateForm({ holidayAllowances: holidayAllowances.map(String) })
              }
            />
          </SettingRow>
        </SettingsSection>
      )}
    </>
  )
}
